<template>
  <v-container fluid grid-list-md>
    <v-row justify="center">
      <v-col cols="10">
        <v-card>
          <v-card-title>
            Import Zones
          </v-card-title>
          <v-card-subtitle>
            Select an Excel file to import zones. The file should have headers: name, description
          </v-card-subtitle>
          <v-card-text>
            <v-col cols="12">
              <v-form
                ref="form"
              >
                <v-row class="my-2 mr-12">
                  <v-col cols="12">
                    <v-file-input
                      v-model="file"
                      truncate-length="40"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      label="Click here to upload an Excel file"
                      @change="handleFileUpload"
                    ></v-file-input>
                  </v-col>
                </v-row>
                <v-row class="mx-8">
                  <v-btn
                    :loading="btnLoading"
                    color="primary"
                    :disabled="file === null"
                    @click="validate"
                  >
                    Import <v-icon right>mdi-upload</v-icon>
                  </v-btn>
                </v-row>
              </v-form>
              <div class="my-12"></div>
              <v-data-table
                v-if="tablePreviewData.length > 0 && headers.length > 1"
                :headers="headers"
                :items="tablePreviewData"
                :items-per-page="25"
                :footer-props="{
                  'items-per-page-options': [10, 25, 100]
                }"
              >
              <template slot="item" slot-scope="props">
                <tr>
                  <template v-for="(header, key) in headers">
                    <td v-if="header.value !== 'Status'" :key="key">{{ props.item[header.value] }}</td>
                  </template>
                  <td>
                    <v-icon v-if="props.item.Status === 'waiting'" color="orange">mdi-minus</v-icon>
                    <v-progress-circular
                      v-else-if="props.item.Status === 'uploading'"
                      color="blue"
                      indeterminate
                    ></v-progress-circular>
                    <v-icon v-else-if="props.item.Status === 'complete'" color="green">mdi-check</v-icon>
                    <v-tooltip top v-else-if="props.item.Status === 'complete duplicate'" color="green">
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="green">mdi-check-underline</v-icon>
                      </template>
                      <span>Zone with this name already exists, updated with new information.</span>
                    </v-tooltip>
                    <v-tooltip top v-else-if="props.item.Status === 'duplicate detected'" color="orange">
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="orange">mdi-progress-alert</v-icon>
                      </template>
                      <span>Zone with this name already exists, attempting to update existing zone.</span>
                    </v-tooltip>
                    <v-tooltip top v-else color="red">
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="red">mdi-close</v-icon>
                      </template>
                      <span>Error: {{ props.item.Status }}</span>
                    </v-tooltip>
                  </td>
                </tr>
              </template>
            </v-data-table>
            </v-col>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <error-snackbar v-model="errorText"></error-snackbar>
  </v-container>
</template>
<script>
import ApiRequests from '@/api/ApiRequests'
import * as XLSX from 'xlsx'
import { ENDPOINT } from '@/shared/constants'
import { mapGetters } from 'vuex'
import axios from 'axios'
import ErrorSnackbar from '@sdgsystems/vue-common/components/ErrorSnackbar.vue'

export default {
  components: {
    ErrorSnackbar
  },

  data () {
    return {
      endpoint: ENDPOINT,
      errorText: '',
      file: null, // Import file
      tablePreviewData: [], // Table data for preview
      headers: [], // Table headers
      detectedHeaders: [], // Detected headers in import file
      selectedHeaders: ['name', 'description'], // Selected headers for mapping (fixed for zones)
      // Uploading
      btnLoading: false,
      reader: new FileReader(),
      parsedData: null
    }
  },

  created () {
    // Headers will be updated when file is loaded
  },

  watch: {
    file: function () {
      if (this.file === null) {
        this.reader.abort()
        this.parsedData = null
        this.tablePreviewData = []
      }
    }
  },

  computed: {
    ...mapGetters([
      'selectedSite'
    ])
  },

  methods: {
    updateHeaders () {
      this.headers = []
      // Find the actual header names from detected headers (case-insensitive match)
      const displayHeaders = []
      for (let i = 0; i < this.detectedHeaders.length; i++) {
        const header = this.detectedHeaders[i]
        if (header) {
          const lower = header.toString().toLowerCase().trim()
          if (lower === 'name' || lower === 'description') {
            displayHeaders.push({ text: header, value: header })
          }
        }
      }
      this.headers = displayHeaders
      this.headers.push({ text: 'Status', value: 'Status' })
    },

    async uploadZones () {
      this.btnLoading = true
      let successfulUploads = 0

      // Fetch all existing zones for the site to check for duplicates
      let existingZones = []
      try {
        const zonesResponse = await ApiRequests.getZones(this.selectedSite._id)
        existingZones = zonesResponse.data
      } catch (err) {
        console.error('Error fetching zones:', err)
        this.errorText = 'Error fetching existing zones'
      }

      for (let i = 0; i < this.tablePreviewData.length; i++) {
        let statusMessage = 'complete'
        this.tablePreviewData[i].Status = 'uploading'

        let zoneName = this.tablePreviewData[i]['name']
        let zoneDescription = this.tablePreviewData[i]['description'] || ''

        // Skip if name is missing
        if (!zoneName || zoneName.toString().trim() === '') {
          this.tablePreviewData[i].Status = 'Error: Name is required'
          continue
        }

        let currZone = {
          name: zoneName.toString().trim(),
          description: zoneDescription.toString().trim(),
          site: this.selectedSite._id
        }

        // Check if zone with this name already exists
        const existingZone = existingZones.find(z => z.name === currZone.name)

        if (existingZone) {
          // Update existing zone
          this.tablePreviewData[i].Status = 'duplicate detected'
          statusMessage += ' duplicate'
          currZone._id = existingZone._id

          await axios.put(ENDPOINT + 'zones/' + currZone._id, currZone)
            .then((response) => {
              successfulUploads++
              console.log('Zone updated:', response)
            })
            .catch(err => {
              this.tablePreviewData[i].Status = err.response.data || err.message || 'Error updating zone'
              console.log('Error updating zone:', err)
            })
        } else {
          // Create new zone
          await axios.post(ENDPOINT + 'zones', currZone)
            .then((res) => {
              successfulUploads++
              // Add the new zone to existingZones array for future checks in this import
              existingZones.push(res.data)
              console.log('Zone created:', res)
            })
            .catch(async err => {
              console.log('Post zone error: ', err.response)
              // Check if error is due to a duplicate (backend validation)
              if (err.response && err.response.status === 400 &&
                  err.response.data && typeof err.response.data.includes === 'function' && err.response.data.includes('duplicate')) {
                this.tablePreviewData[i].Status = 'duplicate detected'
                statusMessage += ' duplicate'
                // Try to find and update the zone
                try {
                  const zonesResponse = await ApiRequests.getZones(this.selectedSite._id)
                  const allZones = zonesResponse.data
                  const foundZone = allZones.find(z => z.name === currZone.name)
                  if (foundZone) {
                    currZone._id = foundZone._id
                    await axios.put(ENDPOINT + 'zones/' + currZone._id, currZone)
                    successfulUploads++
                    console.log('Zone updated after duplicate error:', foundZone)
                  } else {
                    this.tablePreviewData[i].Status = (err.response && err.response.data) || err.message || 'Error creating zone'
                  }
                } catch (updateErr) {
                  this.tablePreviewData[i].Status = (updateErr.response && updateErr.response.data) || updateErr.message || 'Error updating zone'
                  console.log('Error updating zone after duplicate:', updateErr)
                }
              } else {
                this.tablePreviewData[i].Status = (err.response && err.response.data) || err.message || 'Error creating zone'
                console.log('Error creating zone:', err)
              }
            })
        }

        this.tablePreviewData[i].Status = statusMessage
      }
      this.errorText = `${successfulUploads} of ${this.tablePreviewData.length} zones uploaded`
      this.btnLoading = false
    },

    handleFileUpload () {
      const file = this.file

      this.reader.onload = (e) => {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        // Assuming first sheet
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]

        // Parsing sheet data
        const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 })
        this.detectedHeaders = parsedData[0]

        // Trim empty rows
        this.parsedData = parsedData.filter(subArray => subArray.some(item => item !== undefined && item !== null))

        this.prepareTablePreview()
      }
      this.reader.readAsArrayBuffer(file)
    },

    prepareTablePreview () {
      this.tablePreviewData = []
      for (let i = 1; i < this.parsedData.length; i++) {
        let row = {}
        for (let j = 0; j < this.parsedData[i].length; j++) {
          row[this.detectedHeaders[j]] = this.parsedData[i][j]
        }
        row.Status = 'waiting'
        this.tablePreviewData.push(row)
      }
      // Update headers for display - show name and description columns if they exist
      this.updateHeaders()
    },

    validate () {
      if (this.tablePreviewData.length === 0) {
        this.errorText = 'Please select a file with zone data.'
        return
      }

      // Find header indices (case-insensitive)
      let nameHeaderIndex = -1
      let descriptionHeaderIndex = -1

      for (let i = 0; i < this.detectedHeaders.length; i++) {
        const header = this.detectedHeaders[i]
        if (header) {
          const lower = header.toString().toLowerCase().trim()
          if (lower === 'name') {
            nameHeaderIndex = i
          } else if (lower === 'description') {
            descriptionHeaderIndex = i
          }
        }
      }

      if (nameHeaderIndex === -1) {
        this.errorText = 'Excel file must have a "name" column.'
        return
      }

      // Normalize the data to use 'name' and 'description' keys
      const nameHeader = this.detectedHeaders[nameHeaderIndex]
      const descriptionHeader = descriptionHeaderIndex !== -1 ? this.detectedHeaders[descriptionHeaderIndex] : null

      this.tablePreviewData.forEach(row => {
        if (row[nameHeader] !== undefined) {
          row['name'] = row[nameHeader]
        }
        if (descriptionHeader && row[descriptionHeader] !== undefined) {
          row['description'] = row[descriptionHeader]
        } else {
          row['description'] = ''
        }
      })

      this.uploadZones()
    }
  }
}
</script>
<style scoped>
  .v-text-field input::placeholder {
    color: red;
  }
</style>



// WEBPACK FOOTER //
// src/views/ImportZone.vue